<template>
    <div class="speak-to-expert">
        <TransitionModal>
            <Fixed
                v-if="isOpen"
                @close="close"
            >
                <div
                    class="speak-to-expert-modal-scrim"
                    @click.stop.prevent="close"
                ></div>

                <div
                    class="speak-to-expert-modal-content"
                    role="dialog"
                    aria-modal="true"
                    :aria-labelledby="titleElementId"
                >
                    <ThTitle
                        :id="titleElementId"
                        alignment="CENTER"
                        color="var(--blue)"
                    >
                        {{ t('speak_to_expert.title') }}
                    </ThTitle>

                    <ThText alignment="CENTER">
                        {{ t('speak_to_expert.description') }}
                    </ThText>

                    <ul class="options">
                        <li class="option">
                            <!-- TODO: Open the consultation form when it is ready -->
                            <ThLink href="/about-us/contacts">
                                <Icon icon="calendar" />

                                <span class="label">
                                    {{
                                        t(
                                            'speak_to_expert.options.consultation'
                                        )
                                    }}
                                </span>
                            </ThLink>
                        </li>
                        <li
                            class="option"
                            :class="{
                                'mobile-hidden': !isWorkingHours,
                            }"
                            @click.prevent.stop="runAndClose(showIntercom)"
                        >
                            <ThLink>
                                <Icon icon="comments" />

                                <span class="narrow label">
                                    {{ t('speak_to_expert.options.chat') }}
                                </span>
                            </ThLink>
                        </li>

                        <li class="option">
                            <ThLink
                                href="https://wa.me/447916408595"
                                rel="nofollow"
                                target="_blank"
                                :aria-label="`${t('speak_to_expert.options.whatsapp')} - opens in a new tab`"
                            >
                                <i class="whatsapp-icon"></i>

                                <span class="narrow label">
                                    {{ t('speak_to_expert.options.whatsapp') }}
                                </span>
                            </ThLink>
                        </li>

                        <li class="option">
                            <ThLink
                                href="tel:+442031264915"
                                rel="nofollow"
                                target="_blank"
                                :aria-label="`${t('speak_to_expert.options.call')} - opens in a new tab`"
                            >
                                <Icon icon="phone" />

                                <span class="label">
                                    {{ t('speak_to_expert.options.call') }}
                                    <br />
                                    {{
                                        t(
                                            'speak_to_expert.options.phone_number'
                                        )
                                    }}
                                </span>
                            </ThLink>
                        </li>

                        <li class="option">
                            <ThLink href="/about-us/contacts#general-enquiry">
                                <Icon icon="envelope" />

                                <span class="label">
                                    {{ t('speak_to_expert.options.contact') }}
                                </span>
                            </ThLink>
                        </li>
                    </ul>

                    <button
                        class="close"
                        type="button"
                        @click="close"
                    >
                        Close
                    </button>
                </div>
            </Fixed>
        </TransitionModal>
    </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'

import { useSpeakToExpertModalConfig } from './useSpeakToExpertModalConfig'

import Fixed from '@/components/layout/modal/Fixed.vue'
import ThTitle from '@/components/ui/title/ThTitle.vue'
import ThText from '@/components/ui/ThText.vue'
import ThLink from '@/components/ui/buttons/ThLink.vue'
import Icon from '@/components/ui/icon/Icon.vue'
import TransitionModal from '@/components/layout/modal/TransitionModal.vue'

import { useIntercomLauncher } from '@/plugins/intercom/launcher.ts'

const { t } = useI18n()
const { isOpen, close, runAndClose, isWorkingHours } =
    useSpeakToExpertModalConfig()
const { show: showIntercom } = useIntercomLauncher()

const titleElementId = 'speak-to-expert-modal-title-id'
</script>

<style lang="scss" scoped>
.fixed {
    display: flex;
    align-items: center;
    justify-content: center;

    .speak-to-expert-modal-scrim {
        @include scrim;
    }

    .speak-to-expert-modal-content {
        max-width: 1040px;
        width: 100vw;
        height: 100%;
        overflow-y: auto;
        flex-direction: column;
        position: relative;
        z-index: 1002;
        background: $stone-2;
        padding: 60px 20px 50px;

        @include min-width(1024) {
            padding: 100px 60px 80px;
            max-height: 80vh;
            height: initial;
        }

        .close {
            @include close-button;
        }

        .text {
            display: none;

            @include min-width(1024) {
                display: block;
            }
        }

        .options {
            margin: 20px 0 0;
            width: 100%;
            display: flex;
            align-items: flex-start;
            justify-content: center;
            flex-wrap: wrap;

            @include min-width(1024) {
                justify-content: space-between;
                flex-wrap: nowrap;
            }

            .option {
                width: 50%;

                @include min-width(1024) {
                    width: 15%;
                }

                &.mobile-hidden {
                    display: none;

                    @include min-width(1024) {
                        display: initial;
                    }
                }

                &:first-child,
                &.intercom-hidden {
                    width: 100%;

                    @include min-width(1024) {
                        width: 15%;
                    }
                }

                .icon,
                .whatsapp-icon {
                    width: 80px;
                    height: 80px;
                    font-size: 60px;
                    line-height: 60px;
                    margin: 20px auto;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: $blue;
                }

                .whatsapp-icon {
                    &::before {
                        width: 100%;
                        height: 100%;
                        display: block;
                        content: '';
                        background: url('@/assets/icons/whatsapp.svg') no-repeat
                            center;
                        background-size: auto 60px;
                    }
                }

                a,
                button {
                    display: block;
                    text-align: center;
                    font-family: $effra-font-stack;
                    font-weight: 400;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    font-size: 0.75rem;
                    line-height: 1.2em;
                    width: 100%;
                    cursor: pointer;
                }

                .label {
                    width: 120px;
                    display: block;
                    margin: 0 auto;

                    &.narrow {
                        width: 100px;
                    }
                }
            }
        }
    }
}
</style>
